/**
 * `Omit` for union types: removes keys from each member separately, so the union survives.
 *
 * Plain `Omit<T, K>` is `Pick<T, Exclude<keyof T, K>>`, and `keyof (A | B)` holds only the keys
 * that every member shares. Omitting from a union therefore collapses it into one object type
 * and drops every member-specific key:
 *
 *   type A = { kind: 'a'; a: string; c: number };
 *   type B = { kind: 'b'; b: boolean; c: number };
 *
 *   Omit<A | B, 'c'>
 *   // → { kind: 'a' | 'b' }   (`a` and `b` are gone, and `kind` no longer narrows)
 *
 *   DistributiveOmit<A | B, 'c'>
 *   // → { kind: 'a'; a: string } | { kind: 'b'; b: boolean }
 *
 * `T extends unknown ? … : never` is always true. It exists only to make TypeScript distribute
 * the conditional over a union `T`, which evaluates `Omit` once per member
 * and joins the results into a new union.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
